import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  useTheme,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  MenuBook as ExerciseIcon,
  Quiz as QuizIcon,
} from "@mui/icons-material";
import ExerciseStatistics from "../ExerciseStatistics";
import ExamStatistics from "../Statistics";
import PublicMockTestHistoryPanel from "../PublicMockTest/PublicMockTestHistoryPanel";
import {
  STATISTICS_TAB_PARAM,
  DEFAULT_STATISTICS_TAB,
  getStatisticsTabIndex,
  getStatisticsTabKey,
} from "./constants";

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>
    {value === index && children}
  </Box>
);

const MyStatistics = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = getStatisticsTabIndex(searchParams.get(STATISTICS_TAB_PARAM));
  const [tabValue, setTabValue] = useState(initialTab);

  const handleTabChange = (e, v) => {
    setTabValue(v);
    const next = new URLSearchParams(searchParams);
    const key = getStatisticsTabKey(v);
    if (key && key !== DEFAULT_STATISTICS_TAB) next.set(STATISTICS_TAB_PARAM, key);
    else next.delete(STATISTICS_TAB_PARAM);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: { xs: 2.5, sm: 4 },
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <AssessmentIcon sx={{ fontSize: { xs: 28, sm: 36 } }} />
          <Typography
            variant="h4"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.4rem", sm: "2.125rem" } }}
          >
            My Progress
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
          Track your progress across exercises and mock tests
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            "& .MuiTab-root": {
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.95rem" },
              minHeight: 48,
              minWidth: { xs: "auto", sm: 90 },
              px: { xs: 1.25, sm: 2 },
            },
          }}
        >
          <Tab
            icon={<ExerciseIcon />}
            iconPosition="start"
            label="Exercises"
          />
          <Tab
            icon={<TrophyIcon />}
            iconPosition="start"
            label="Exams"
          />
          <Tab
            icon={<QuizIcon />}
            iconPosition="start"
            label="Mock Tests"
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <ExerciseStatistics />
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <ExamStatistics />
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <PublicMockTestHistoryPanel />
      </TabPanel>
    </Container>
  );
};

export default MyStatistics;
